import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import { z } from "zod"
import type { ClientsModel, ClientTableFilters } from "@/src/types/clientsModel"
import type { GridSortModel, GridRowSelectionModel, GridColumnVisibilityModel } from "@mui/x-data-grid"
import type { PaginationState, BaseStoreState } from "../types"
import { useGetClients, uploadClientLogo } from "@/src/services/clients"
import { useAuthStore } from "./auth.slice"

// Zod schemas for validation
const ClientTableFiltersSchema = z.object({
    status: z.array(z.string()),
})

const ClientsModelSchema = z.object({
    id: z.string(),
    name: z.string().min(1, "Client name is required"),
    websiteLink: z.string().url("Invalid website URL").optional().or(z.literal("")),
    aboutClient: z.string().optional(),
    email: z.string().email("Invalid email address").optional().or(z.literal("")),
    logoUrl: z.string().optional(),
})

// Store state interface
export interface ClientsStoreState extends BaseStoreState {
    clients: ClientsModel[]
    currentClient: ClientsModel | null
    filters: ClientTableFilters
    pagination: PaginationState
    sortModel: GridSortModel
    selectedRows: GridRowSelectionModel
    columnVisibility: GridColumnVisibilityModel
    searchQuery: string
    tabData: { [key: string]: number } | undefined
}

// Store actions interface
export interface ClientsStoreActions {
    // Client management
    fetchClients: (filters?: ClientTableFilters) => Promise<void>
    setCurrentClient: (id: string) => void
    createClient: (data: Partial<ClientsModel>) => Promise<void>
    updateClient: (id: string, data: Partial<ClientsModel>) => Promise<void>
    deleteClient: (id: string) => Promise<void>
    uploadClientLogo: (clientId: string, file: File) => Promise<void>

    // Filters and pagination
    updateFilters: (filters: Partial<ClientTableFilters>) => void
    setPagination: (pagination: Partial<PaginationState>) => void
    setSortModel: (sortModel: GridSortModel) => void
    setSelectedRows: (rows: GridRowSelectionModel) => void
    setColumnVisibility: (visibility: GridColumnVisibilityModel) => void
    setSearchQuery: (query: string) => void
}

export type ClientsStore = ClientsStoreState & ClientsStoreActions

// Default filters
const defaultFilters: ClientTableFilters = {
    status: [],
}

export const useClientsStore = create<ClientsStore>()(
    immer((set, get) => ({
        // State
        clients: [],
        currentClient: null,
        filters: defaultFilters,
        pagination: { page: 0, pageSize: 40 },
        sortModel: [],
        selectedRows: [],
        columnVisibility: { category: false },
        searchQuery: "",
        tabData: undefined,
        loading: false,
        error: null,

        // Actions
        fetchClients: async (filters?: ClientTableFilters) => {
            set((state) => {
                state.loading = true
                state.error = null
            })

            try {
                const { pagination, sortModel, searchQuery } = get()
                const targetFilters = filters || get().filters
                const { user } = useAuthStore.getState()

                if (!user?.activeWorkspace?.id) {
                    throw new Error("No active workspace found")
                }

                // Use the existing useGetClients hook logic
                const { clients, clientsCount, clientsLoading, tabData } = useGetClients(
                    pagination.page,
                    pagination.pageSize,
                    sortModel,
                    searchQuery,
                    [], // filterOptions
                    targetFilters
                )

                set((state) => {
                    state.clients = clients
                    state.tabData = tabData
                    state.loading = clientsLoading
                    state.error = null
                })
            } catch (error) {
                set((state) => {
                    state.loading = false
                    state.error = error instanceof Error ? error.message : "Failed to fetch clients"
                })
            }
        },

        setCurrentClient: (id: string) => {
            const { clients } = get()
            const client = clients.find((c) => c.id === id)

            set((state) => {
                state.currentClient = client || null
            })
        },

        createClient: async (data: Partial<ClientsModel>) => {
            set((state) => {
                state.loading = true
                state.error = null
            })

            try {
                // Client data validation can be added later if needed
                const { user } = useAuthStore.getState()

                if (!user?.activeWorkspace?.id) {
                    throw new Error("No active workspace found")
                }

                // This would typically call an API to create client
                // For now, we'll add to local state
                const newClient: ClientsModel = {
                    name: data.name || "",
                    websiteLink: data.websiteLink || "",
                    aboutClient: data.aboutClient || "",
                    email: data.email || "",
                    logoUrl: data.logoUrl || "",
                }

                set((state) => {
                    state.clients.unshift(newClient)
                    state.loading = false
                    state.error = null
                })
            } catch (error) {
                set((state) => {
                    state.loading = false
                    state.error = error instanceof Error ? error.message : "Failed to create client"
                })
                throw error
            }
        },

        updateClient: async (id: string, data: Partial<ClientsModel>) => {
            set((state) => {
                state.loading = true
                state.error = null
            })

            try {
                // Client data validation can be added later if needed
                // This would typically call an API to update client
                // For now, we'll update local state

                set((state) => {
                    const clientIndex = state.clients.findIndex((c) => c.id === id)
                    if (clientIndex !== -1) {
                        state.clients[clientIndex] = { ...state.clients[clientIndex], ...data }
                    }
                    if (state.currentClient?.id === id) {
                        state.currentClient = { ...state.currentClient, ...data }
                    }
                    state.loading = false
                    state.error = null
                })
            } catch (error) {
                set((state) => {
                    state.loading = false
                    state.error = error instanceof Error ? error.message : "Failed to update client"
                })
                throw error
            }
        },

        deleteClient: async (id: string) => {
            set((state) => {
                state.loading = true
                state.error = null
            })

            try {
                // This would typically call an API to delete client
                // For now, we'll remove from local state

                set((state) => {
                    state.clients = state.clients.filter((c) => c.id !== id)
                    if (state.currentClient?.id === id) {
                        state.currentClient = null
                    }
                    state.loading = false
                    state.error = null
                })
            } catch (error) {
                set((state) => {
                    state.loading = false
                    state.error = error instanceof Error ? error.message : "Failed to delete client"
                })
                throw error
            }
        },

        uploadClientLogo: async (clientId: string, file: File) => {
            set((state) => {
                state.loading = true
                state.error = null
            })

            try {
                const { clients, currentClient } = get()
                // Get the actual client data from store to avoid overwriting with empty strings
                const client = clients.find((c) => c.id === clientId) || currentClient

                if (!client || client.id !== clientId) {
                    throw new Error("Client not found")
                }

                const response = await uploadClientLogo(file, client)

                if (response.status === "success" && response.data) {
                    const updatedClient = response.data
                    set((state) => {
                        const clientIndex = state.clients.findIndex((c) => c.id === clientId)
                        if (clientIndex !== -1) {
                            state.clients[clientIndex].logoUrl = updatedClient.logoUrl
                        }
                        if (state.currentClient?.id === clientId) {
                            state.currentClient.logoUrl = updatedClient.logoUrl
                        }
                        state.loading = false
                        state.error = null
                    })
                } else {
                    throw new Error(response.errorMessage || "Failed to upload logo")
                }
            } catch (error) {
                set((state) => {
                    state.loading = false
                    state.error = error instanceof Error ? error.message : "Failed to upload logo"
                })
                throw error
            }
        },

        updateFilters: (filters: Partial<ClientTableFilters>) => {
            set((state) => {
                state.filters = { ...state.filters, ...filters }
                // Reset pagination to first page when filters change to avoid showing empty results
                // This ensures users always see data after applying filters
                state.pagination.page = 0
                state.error = null
            })
        },

        setPagination: (pagination: Partial<PaginationState>) => {
            set((state) => {
                state.pagination = { ...state.pagination, ...pagination }
            })
        },

        setSortModel: (sortModel: GridSortModel) => {
            set((state) => {
                state.sortModel = [...sortModel]
            })
        },

        setSelectedRows: (rows: GridRowSelectionModel) => {
            set((state) => {
                state.selectedRows = [...rows]
            })
        },

        setColumnVisibility: (visibility: GridColumnVisibilityModel) => {
            set((state) => {
                state.columnVisibility = visibility
            })
        },

        setSearchQuery: (query: string) => {
            set((state) => {
                state.searchQuery = query
            })
        },
    }))
)
